const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/utils/data.ts';
import _ from 'lodash';
import { KeyValuePair } from '../defs/generalDefs';

export type FilterFunction<Type = any> = (a: Type) => boolean;

export const filterObject = (object: KeyValuePair, filterFunction: FilterFunction): KeyValuePair => {
    const filteredObject: KeyValuePair = {};
    Object.keys(object).forEach(key => {
        const item = object[key];
        if (filterFunction(item)) {
            filteredObject[key] = item;
        }
    });
    return filteredObject;
};

export type SortResult = -1 | 0 | 1;

export type SortFunction<Type = any> = (a: Type, b: Type) => SortResult;

/* Can provide multiple paths; if the first path results in equality, the next path will be evaluated */
export const sortObjectsByPath = (
    path: string | string[],
    descending: boolean | boolean[] = false,
    convertFunction: ((value: any) => any) | undefined = undefined,
    emptyValuesAtEnd: boolean = false
) => {
    const getValue = (object: KeyValuePair, path: string) => {
        // Without empty string as default value, sorting does not work properly
        let value = _.get(object, path, '');
        if (convertFunction) {
            if (!(value === undefined || value === null || value === '')) {
                value = convertFunction(value);
            }
        }
        return value;
    };

    const paths = Array.isArray(path) ? path : [path];
    const descendings = Array.isArray(descending) ? descending : [descending];

    return (object1: KeyValuePair, object2: KeyValuePair) => {
        const getSortResult = (
            object1: KeyValuePair,
            object2: KeyValuePair,
            paths: string[],
            descendings: boolean[]
        ): SortResult => {
            const path = paths[0];
            const descending = descendings[0];

            const firstValue = getValue(object1, path);
            const secondValue = getValue(object2, path);

            if (firstValue === secondValue) {
                return paths.length === 1 ? 0 : getSortResult(object1, object2, paths.slice(1), descendings.slice(1));
            }

            // Empty strings are considered smaller, but they should show up at the end
            if (emptyValuesAtEnd) {
                if (firstValue === '') return 1;
                if (secondValue === '') return -1;
            }

            if (typeof firstValue === 'string' && typeof secondValue === 'string') {
                // sensitivity: 'base' makes the comparison case- and accent-insensitive
                // ("É" and "e" sort together), which is what users expect for
                // human-facing lists and is harmless for ID-shaped strings.
                const result = firstValue.localeCompare(secondValue, undefined, { sensitivity: 'base' });
                return (descending ? -1 * result : result) as SortResult;
            }

            if (firstValue > secondValue) return descending ? -1 : 1;
            if (firstValue < secondValue) return descending ? 1 : -1;
            return 0;
        };

        return getSortResult(object1, object2, paths, descendings);
    };
};

export const filterObjectsByPath = (path: string | string[], expectedValue: any | any[]) => {
    const caseSensitive = false;
    const allowEmptyStringValue = true;
    const stringCompare: 'includes' | 'startsWith' = 'includes';

    const paths = Array.isArray(path) ? path : [path];
    const expectedValues = Array.isArray(expectedValue) ? expectedValue : [expectedValue];

    if (paths.length !== expectedValues.length) {
        throw new Error(
            `Should provide an expected value for each path. ${paths.length} paths, but ${expectedValues.length} expected values specified`
        );
    }

    return <ItemType>(item: ItemType) => {
        return paths.reduce((accumulator, path, currentIndex) => {
            if (!accumulator) return accumulator;

            const expectedValue = expectedValues[currentIndex];
            const value = _.get(item, path);
            if (typeof expectedValue === 'string' && typeof value === 'string') {
                const trimmedValue = value.trim();
                if (allowEmptyStringValue && trimmedValue === '') return true;

                let compareValue = trimmedValue;
                let expectedCompareValue = expectedValue;
                if (!caseSensitive) {
                    compareValue = trimmedValue.toLowerCase();
                    expectedCompareValue = expectedValue.toLowerCase();
                }
                if (compareValue === expectedCompareValue) return true;
                if (stringCompare === 'includes') {
                    return compareValue.includes(expectedCompareValue);
                } else {
                    if (compareValue.startsWith(expectedCompareValue)) {
                        return true;
                    } else if (compareValue.includes(` ${expectedCompareValue}`)) {
                        return true;
                    }
                    return false;
                }
            } else {
                return value === expectedValue;
            }
        }, true);
    };
};